






























import mixins from "vue-typed-mixins"
import { each, every, find, first, map } from 'lodash-es'

import { LayoutGroupExt, PropExtInfo } from "models"
import { DisplayLookup, LayoutLookup } from "models/layout"
import { AnyPropLookup, PropChoice } from "models/schema"
import { FullSchema } from 'models/schema/stay'

import MRC from './MRC.vue'
import DisplayMixin from "@mixins/Display.vue"
import InputMixin from "@mixins/Input.vue"
import Generic from "@sections/Generic.vue"

interface PropChoice_Ext extends Omit<PropChoice, 'value'> {
    value: number | string | boolean
}

const displayInfo: DisplayLookup = {
    all: [
        {
            field: "strength_right_normal",
            force_label: "Right side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "strength_left_normal",
            force_label: "Left side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "facial_strength_right",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "facial_strength_left",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "arm_strength_right",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "arm_strength_left",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "leg_strength_right",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "leg_strength_left",
            dropdown: true,
            full_width: true,
            hide_label: true
        },
        {
            field: "ataxia_right_normal",
            force_label: "Right side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "ataxia_left_normal",
            force_label: "Left side normal",
            booleanOptions: {
                combined: true
            }
        },
        {
            field: "ataxia_arm_right",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        },
        {
            field: "ataxia_arm_left",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        },
        {
            field: "ataxia_leg_right",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        },
        {
            field: "ataxia_leg_left",
            dropdown: true,
            full_width: true,
            hide_label: true,
            booleanOptions: {
                false_label: "No ataxia",
                true_label: "Ataxia"
            }
        }
    ]
}

const layouts: LayoutLookup = {
    strength: [
        { fields: [ "strength_right_normal", "strength_left_normal" ], label: " ", label_col: 2, alignment: "inline" },
        { fields: [ "facial_strength_right", "facial_strength_left" ], label: "Facial strength", label_col: 2, alignment: "inline" },
        { fields: [ "arm_strength_right", "arm_strength_left" ], label: "Arm strength", label_col: 2, field_col: 5, alignment: "inline" },
        { fields: [ "leg_strength_right", "leg_strength_left" ], label: "Leg strength", label_col: 2, field_col: 5, alignment: "inline" }
    ],
    ataxia: [
        { fields: [ "ataxia_right_normal", "ataxia_left_normal" ], label: " ", label_col: 2, alignment: "inline" },
        { fields: [ "ataxia_arm_right", "ataxia_arm_left" ], label: "Arm coordination", label_col: 2, field_col: 5, alignment: "inline" },
        { fields: [ "ataxia_leg_right", "ataxia_leg_left" ], label: "Leg coordination", label_col: 2, field_col: 5, alignment: "inline" },
    ]
}

type Link = [string, string[]]

export default mixins(DisplayMixin, InputMixin).extend({
    components: {
        Generic,
        mrc: MRC
    },
    props: {
        parentHtmlId: {
            type: String,
            required: true
        },
        stay_id: {
            type: Number,
            required: true
        },
        nihss_id: {
            type: Number,
            required: true
        },
    },
    data() {
        let section: keyof FullSchema = 'nihss_set'
        return {
            section,
        }
    },
    computed: {
        schema(): AnyPropLookup | undefined {
            return this.getChildSchema(this.section)
        },
        statusMsg(): string {
            return this.schema ? "" : "Schema loading..."
        },
        allFields(): PropExtInfo[] {
            return this.schema
                ? this.makePropExtInfo_NIHSS(this.schema, displayInfo.all, this.nihss_id)
                : []
        },
        strengthGroups(): LayoutGroupExt[] | undefined {
            return this.makeGroups_NIHSS(this.allFields, layouts.strength, this.nihss_id)
        },
        ataxiaGroups(): LayoutGroupExt[] | undefined {
            return this.makeGroups_NIHSS(this.allFields, layouts.ataxia, this.nihss_id)
        },
        /** NOTE: this gets replaced in beforeCreate, but we declare it here to accommodate Typescript prop check */
        links(): Link[] {
            return []
        }
    },
    beforeCreate() {
        const sides = ['right', 'left']
        const strengths = ['facial_strength', 'arm_strength', 'leg_strength']
        const ataxias = ['ataxia_arm', 'ataxia_leg']

        const props: string[] = []
        const links: Link[] = []

        each(sides, side => {
            const strengthNormal = `strength_${side}_normal`
            const strengthTargets = map(strengths, base => { return `${base}_${side}` })
            links.push([strengthNormal, strengthTargets])

            const ataxiaNormal = `ataxia_${side}_normal`
            const ataxiaTargets = map(ataxias, base => { return `${base}_${side}`})
            links.push([ataxiaNormal, ataxiaTargets])

            props.push(strengthNormal, ...strengthTargets, ataxiaNormal, ...ataxiaTargets)
        })

        const self = this
        each(props, prop => {
            const computed: any = {
                get() {
                    return this.getArrayedObjVal(this.stay_id, 'nihss_set', this.nihss_id, prop)
                }
            }
            self.$options.computed![prop] = computed
        })

        this.$options.computed!['links'] = {
            get() { return links }
        }
    },
    created() {
        this.generateWatchers()
    },
    methods: {
        getPropAndFirstChoice(fieldName: string): [PropExtInfo, PropChoice_Ext | undefined] {
            const propInfo = find(this.allFields, { name: fieldName })
            let choice: PropChoice_Ext | undefined
            if (Array.isArray(propInfo?.choices))
                choice = first(propInfo?.choices)
            else
                choice = { display_name: '', value: false }
            return [propInfo!, choice]
        },

        generateWatchers() {
            const vm = this
            each(this.links, link => {
                const normal = link[0]
                const targets = link[1]

                this.$watch(normal, function(val: any, oldVal: any) {
                    if (val) {
                        each(targets, target => {
                            const res = this.getPropAndFirstChoice(target)
                            const propInfo = res[0], choice = res[1]
                            this.setArrayedObjVal(this.stay_id, 'nihss_set', this.nihss_id, propInfo.nested_path!, choice ? choice.value : null)
                        })
                    }
                })

                function allTargetsNormal() {
                    return every(targets, target => {
                        const res = vm.getPropAndFirstChoice(target)
                        const choice = res[1]
                        const val = vm.getArrayedObjVal(vm.stay_id, 'nihss_set', vm.nihss_id, target)
                        const sameChoice = choice ? choice.value === val : false
                        return sameChoice
                    })
                }
                each(targets, target => {
                    this.$watch(target, function(val: any, oldVal: any) {
                        const res = this.getPropAndFirstChoice(target)
                        const choice = res[1]
                        const normalPropInfo = find(this.allFields, { name: normal })
                        const allNormal = allTargetsNormal()
                        this.setArrayedObjVal(this.stay_id, 'nihss_set', this.nihss_id, normalPropInfo!.nested_path!, allNormal)
                    })
                })
            })
        }
    },
})
